/**
 * Ageing card for open advance (ENG-27316): how much held credit has how long
 * left before it expires, read from each entry's `expires_at` (see
 * bucketByExpiry) — so the card never assumes the hold period.
 *
 * One row per bucket with a bar sized to its share of the total, most time left
 * first and expired last, so it reads like an age scale.
 */
import { Box, LinearProgress, Typography } from "@mui/material";

import type { WalletEntry } from "../types/wallet";
import { bucketByExpiry } from "../utils/openAdvance";
import { formatAmount } from "../utils/formatters";
import { V2_COLORS } from "@/pages/InvoiceManagement/v2/constants/colors";

interface ExpiryAgeingCardProps {
  entries: WalletEntry[];
  today: Date;
}

export default function ExpiryAgeingCard({ entries, today }: ExpiryAgeingCardProps) {
  // bucketByExpiry returns nearest-first: [0–10, 11–20, 21–30, Expired].
  const [nearest, middle, furthest, expired] = bucketByExpiry(entries, today);
  const rows = [
    { bucket: furthest, color: V2_COLORS.primary },
    { bucket: middle, color: V2_COLORS.primarySoft },
    { bucket: nearest, color: V2_COLORS.warning },
    { bucket: expired, color: V2_COLORS.error },
  ];
  const total = rows.reduce((sum, r) => sum + r.bucket.amount, 0);

  return (
    <Box sx={{ bgcolor: "background.paper", borderRadius: "8px", p: 2.5 }}>
      <Typography fontWeight={700} fontSize="16px">
        Open advance by time left
      </Typography>
      <Typography fontSize="12px" color="text.secondary" mt={0.25}>
        Held credit by how long it has before it expires
      </Typography>
      <Box display="flex" flexDirection="column" gap={1.75} mt={2}>
        {rows.map(({ bucket, color }) => (
          <Box key={bucket.label} data-testid="ageing-bucket">
            <Box display="flex" justifyContent="space-between" alignItems="baseline" gap={2}>
              <Typography fontSize="13px">{bucket.label}</Typography>
              <Typography fontSize="13px" sx={{ whiteSpace: "nowrap" }}>
                <Box component="span" fontWeight={700}>
                  {formatAmount(bucket.amount)}
                </Box>{" "}
                · {bucket.count} {bucket.count === 1 ? "entry" : "entries"}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={total > 0 ? (bucket.amount / total) * 100 : 0}
              aria-label={`${bucket.label} share`}
              sx={{
                mt: 0.75,
                height: 6,
                borderRadius: 3,
                bgcolor: V2_COLORS.bgMuted,
                "& .MuiLinearProgress-bar": { bgcolor: color, borderRadius: 3 },
              }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
}
